/**
 * Bottle art — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_paint_crafts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const bottleArtPack: ToilePack = {
    slug: 'bottle-art',
    category: 'craft',
    name: 'Bottle art',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'dots', label: 'Dot mandala', subject: 'a painted bottle with a dot mandala'},
        {key: 'madhubani', label: 'Madhubani', subject: 'a bottle painted in Madhubani style'},
        {key: 'jute', label: 'Jute', subject: 'a bottle wrapped in jute with flowers'},
    ],
    styles: [
        {key: 'shelf', label: 'Home shelf', style: 'displayed on a wooden wall shelf at home with a small potted plant and a stack of books', palette: 'Warm wood and green leaves around the decorated bottle.', framing: 'Shot at eye level, the bottle centred and standing tall in the frame.'},
        {key: 'vase', label: 'As a vase', style: 'used as a vase holding a few fresh marigolds and leaves on a cotton table runner', palette: 'Bright marigold orange and green, the runner soft and plain.', framing: 'Shot at table height, the bottle in the centre with the flowers rising into the upper third.'},
        {key: 'workdesk', label: 'Craft desk', style: 'just finished on a craft desk beside acrylic paint pots, a dotting tool, a ball of jute twine and fine brushes', palette: 'Pale wood and paint-pot colours, cheerful and handmade.', framing: 'Shot from a slight high angle, the bottle centred with the supplies around its base.'},
    ],
    directions: ['fairy lights', 'Warli art', 'Diwali'],
    directionHint: 'try: fairy lights',
    seo: {
        title: 'Bottle art: dot mandala, Madhubani, jute',
        metaDescription: 'Nine bottle art ideas, a dot mandala, a Madhubani-style bottle and a jute-wrapped bottle, on a shelf, as a vase and on a craft desk.',
        h1: 'Bottle art ideas: dot mandala, Madhubani, jute-wrapped bottle',
        tagline: 'Three decorated bottles, each on a home shelf, holding flowers as a vase and beside its craft supplies.',
        intro: 'Old bottles can be painted or wrapped at home and kept as decor. These nine show three decorated bottles: one with a dot mandala, one painted in Madhubani style and one wrapped in jute with flowers. Each stands on a wooden shelf, holds marigolds as a vase, or sits on a craft desk beside its paint and twine. Type another design, such as fairy lights or Warli art, to ask for it.',
        useCases: [
            'a shelf decoration at home',
            'a vase for fresh flowers',
            'a recycling craft project',
            'a table decoration for Diwali',
        ],
        faq: [
            {q: 'What do the nine pictures show?', a: 'A dot mandala, a Madhubani-style bottle and a jute-wrapped bottle, each shown on a shelf, holding flowers and on a craft desk.'},
            {q: 'Are these photos of real bottles?', a: 'No. They are generated pictures of each design, not photographs of a particular bottle.'},
            {q: 'Can I ask for a different design?', a: 'Yes. Type a design such as fairy lights, Warli art or Diwali. A typed design is a request, not a promised result.'},
        ],
    },
}
